import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Droplets, ShieldCheck, MapPin, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { useApp } from "../context/AppContext";
import { sendOtp, verifyOtp, saveProfile, formatApiError } from "../lib/api";
import { IMAGES } from "../config/images";

const STEPS = ["Mobile", "OTP", "Name", "City", "Vehicle"];

export default function LoginPage() {
  const { config, signIn, customer, openBooking } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [phone, setPhone] = useState(customer?.phone || "");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [token, setToken] = useState("");
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState(customer?.name || "");
  const [city, setCity] = useState(customer?.city || "");
  const [vehicle, setVehicle] = useState(customer?.vehicle_type || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const liveCities = config.cities.filter((c) => c.live);

  const send = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await sendOtp(phone, "customer");
      setDevOtp(res.dev_otp || "");
      setStep(1);
      toast.success(res.demo_mode ? "OTP generated (demo mode — shown below)" : "OTP sent to your mobile");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await verifyOtp(phone, otp, "customer");
      setToken(res.token);
      setProfile(res.user);
      if (!res.user.is_new && res.user.name) {
        signIn(res.token, res.user);
        setStep(5);
      } else {
        setStep(2);
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await saveProfile(token, { name, city, vehicle_type: vehicle });
      signIn(token, res.user);
      setStep(5);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const chip = (active) =>
    `rounded-xl border px-4 py-3 text-[14px] font-medium transition-all ${
      active ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25"
    }`;

  const valid = [
    phone.replace(/\D/g, "").length === 10,
    otp.trim().length >= 4,
    name.trim().length >= 2,
    !!city,
    !!vehicle,
  ];

  return (
    <main className="flex min-h-screen bg-ink" data-testid="login-page">
      {/* form panel */}
      <div className="flex w-full flex-col px-6 py-8 sm:px-12 lg:w-[46%] lg:px-16">
        <div className="flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-[13px] text-mist transition-colors hover:text-paper">Skip for now</Link>
        </div>

        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
          <div className="lg:hidden">
            <h1 className="font-display text-3xl font-bold leading-tight text-paper">
              Cleaner Cars. <span className="text-carza-lite text-glow">Happier Journeys.</span>
            </h1>
            <p className="mt-2 text-sm text-mist">Professional car care at your doorstep.</p>
          </div>

          {step < 5 && (
            <div className="mt-8 flex items-center gap-2" data-testid="login-progress">
              {STEPS.map((s, i) => (
                <div key={s} className="flex-1">
                  <div className={`h-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-carza" : "bg-white/10"}`} />
                  <p className={`mt-1.5 font-mono text-[9px] uppercase tracking-widest ${i === step ? "text-carza-lite" : "text-mist-2"}`}>{s}</p>
                </div>
              ))}
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }} className="mt-8">
              {step === 0 && (
                <div className="flex flex-col gap-4">
                  <h2 className="font-display text-2xl font-bold text-paper">Welcome to CARZA</h2>
                  <p className="text-sm text-mist">Professional car care at your doorstep.</p>
                  <label className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#180b30] px-4 py-3.5">
                    <span className="flex items-center gap-1.5 border-r border-white/10 pr-3 text-sm text-paper">🇮🇳 +91</span>
                    <input
                      className="w-full bg-transparent text-sm text-paper outline-none placeholder:text-mist-2/70"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="Enter your mobile number"
                      inputMode="numeric"
                      autoFocus
                      data-testid="login-phone-input"
                    />
                  </label>
                  <button onClick={send} disabled={!valid[0] || loading} className="btn btn-primary w-full py-4 text-[15px] disabled:opacity-40" data-testid="login-get-started-btn">
                    {loading ? "Sending OTP…" : <>Get Started <ArrowRight size={16} /></>}
                  </button>
                  <div className="flex items-center gap-3 py-1">
                    <span className="h-px flex-1 bg-white/10" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-mist-2">or continue with</span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                  <button
                    onClick={() => toast.info("Google sign-in is coming soon — use your mobile number for now.")}
                    className="btn btn-outline w-full py-3.5"
                    data-testid="login-google-btn"
                  >
                    <GoogleG /> Continue with Google
                  </button>
                  <p className="text-center text-[11px] leading-relaxed text-mist-2">
                    By continuing, you agree to our <Link to="/terms" className="underline">Terms</Link> &amp; <Link to="/privacy" className="underline">Privacy Policy</Link>.
                  </p>
                </div>
              )}

              {step === 1 && (
                <div className="flex flex-col gap-4">
                  <h2 className="font-display text-2xl font-bold text-paper">Verify your number</h2>
                  <p className="text-sm text-mist">OTP sent to +91 {phone}. <button onClick={() => setStep(0)} className="text-carza-lite underline">Change</button></p>
                  <input
                    className="field text-center font-mono text-xl tracking-[0.5em]"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="••••••"
                    inputMode="numeric"
                    autoFocus
                    data-testid="login-otp-input"
                  />
                  {devOtp && (
                    <p className="rounded-xl border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-center font-mono text-xs text-amber-200" data-testid="login-demo-otp">
                      Demo mode — your OTP is {devOtp}
                    </p>
                  )}
                  <button onClick={verify} disabled={!valid[1] || loading} className="btn btn-primary w-full py-4 disabled:opacity-40" data-testid="login-verify-btn">
                    {loading ? "Verifying…" : <>Verify OTP <ArrowRight size={16} /></>}
                  </button>
                  <button onClick={send} className="btn btn-ghost text-[13px]">Resend OTP</button>
                </div>
              )}

              {step === 2 && (
                <div className="flex flex-col gap-4">
                  <h2 className="font-display text-2xl font-bold text-paper">What should we call you?</h2>
                  <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" autoFocus autoComplete="name" data-testid="login-name-input" />
                  <button onClick={() => (valid[2] ? setStep(3) : setError("Please enter your name"))} disabled={!valid[2]} className="btn btn-primary w-full py-4 disabled:opacity-40" data-testid="login-name-next-btn">
                    Continue <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {step === 3 && (
                <div className="flex flex-col gap-4">
                  <h2 className="font-display text-2xl font-bold text-paper">Your city</h2>
                  <div className="flex flex-wrap gap-2">
                    {liveCities.map((c) => (
                      <button key={c.name} onClick={() => setCity(c.name)} className={chip(city === c.name)} data-testid={`login-city-${c.name.toLowerCase().replace(/\s/g, "-")}`}>
                        {c.name}
                      </button>
                    ))}
                    {config.cities.filter((c) => !c.live).map((c) => (
                      <span key={c.name} className="rounded-xl border border-white/5 px-4 py-3 text-[14px] font-medium text-mist-2" data-testid={`login-city-${c.name.toLowerCase().replace(/\s/g, "-")}-soon`}>
                        {c.name} · soon
                      </span>
                    ))}
                  </div>
                  <button onClick={() => (city ? setStep(4) : setError("Please pick your city"))} disabled={!city} className="btn btn-primary w-full py-4 disabled:opacity-40" data-testid="login-city-next-btn">
                    Continue <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {step === 4 && (
                <div className="flex flex-col gap-4">
                  <h2 className="font-display text-2xl font-bold text-paper">Your vehicle type</h2>
                  <div className="flex flex-wrap gap-2">
                    {config.vehicles.map((v) => (
                      <button key={v.name} onClick={() => setVehicle(v.name)} className={chip(vehicle === v.name)} data-testid={`login-vehicle-${v.name.toLowerCase().split(" ")[0].replace("/", "-").trim()}`}>
                        {v.name}
                      </button>
                    ))}
                  </div>
                  <button onClick={finish} disabled={!vehicle || loading} className="btn btn-primary w-full py-4 disabled:opacity-40" data-testid="login-finish-btn">
                    {loading ? "Setting up…" : <>Get Started <Check size={16} /></>}
                  </button>
                </div>
              )}

              {step === 5 && profile && (
                <div className="flex flex-col items-center gap-5 py-6 text-center" data-testid="login-success">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-carza-dim text-carza-lite"><Check size={28} /></span>
                  <div>
                    <h2 className="font-display text-2xl font-bold text-paper">Welcome aboard, {(profile.name || name).split(" ")[0]}.</h2>
                    <p className="mt-2 text-sm text-mist">Your CARZA account is ready. Booking takes under 60 seconds.</p>
                  </div>
                  <button onClick={() => { navigate("/"); setTimeout(() => openBooking(), 400); }} className="btn btn-primary w-full py-4" data-testid="login-book-first-btn">
                    Book My First Wash <ArrowRight size={16} />
                  </button>
                  <button onClick={() => navigate("/")} className="btn btn-ghost text-[13px]">Explore CARZA first</button>
                </div>
              )}

              {error && step < 5 && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="login-error">{error}</p>}

              {step > 0 && step < 5 && (
                <button onClick={() => { setError(""); setStep(step - 1); }} className="btn btn-ghost mt-2 self-start text-[13px]" data-testid="login-back-btn">
                  <ArrowLeft size={14} /> Back
                </button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* visual panel */}
      <div className="relative hidden flex-1 overflow-hidden lg:block" data-testid="login-visual">
        <img src={IMAGES.loginIndia} alt="An Indian residential society at dusk with parked cars" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(200deg, rgba(19,8,38,0.25) 0%, rgba(19,8,38,0.85) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(70% 60% at 30% 20%, rgba(124,77,255,0.25), transparent 70%)" }} />
        <div className="absolute bottom-14 left-14 right-14">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}>
            <h1 className="max-w-md font-display text-5xl font-bold leading-[1.1] tracking-tight text-paper">
              Cleaner Cars. <span className="text-carza-lite text-glow">Happier Journeys.</span>
            </h1>
            <p className="mt-4 max-w-sm text-base text-mist">Professional car care at your doorstep.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {[
                { icon: Droplets, label: "Water-Efficient" },
                { icon: MapPin, label: "Doorstep Service" },
                { icon: ShieldCheck, label: "Trained Partners" },
                { icon: Sparkles, label: "Standardized SOPs" },
              ].map(({ icon: Icon, label }, i) => (
                <span key={label} className="glass float-y flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-medium text-paper" style={{ animationDelay: `${i * 0.6}s` }}>
                  <Icon size={13} className="text-carza-lite" /> {label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
}

function GoogleG() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z" />
      <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z" />
      <path fill="#FBBC05" d="M5.2 14.3c-.3-.8-.4-1.6-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z" />
      <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 3c.9-3 3.6-5.1 6.8-5.1z" />
    </svg>
  );
}
